import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaMoneyBillWave, FaFileAlt, FaSyringe } from "react-icons/fa";
import moment from "moment";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetSurgeryComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petSurgeries, setPetSurgeries] = useState([]);
    const [surgeries, setSurgeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingSurgery, setEditingSurgery] = useState(null);
    const [deletingSurgeryId, setDeletingSurgeryId] = useState(null);
    const [selectedSurgery, setSelectedSurgery] = useState(null);
    const [surgeryPrice, setSurgeryPrice] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [hasSurgeryForAppointment, setHasSurgeryForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const canDelete = () => {
        return userRole === 'Admin' || hasPermission("Appointment Surgery Delete");
    };

    // Helper function to format price without .00 if whole number
    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        const num = parseFloat(price);
        if (isNaN(num)) return '0';
        return num % 1 === 0 ? num.toString() : num;
    };

    // Show toast message
    const showToast = (icon, title, message = '') => {
        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
        Toast.fire({
            icon: icon,
            title: title,
            text: message
        });
    };

    useEffect(() => {
        fetchPetSurgeries();
    }, [appointmentId, petId]);

    const fetchPetSurgeries = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-surgery/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                const surgeriesForPet = response.data.petSurgeries || [];
                setPetSurgeries(surgeriesForPet);
                setSurgeries(response.data.surgeries || []);
                const hasSurgery = surgeriesForPet.some(surgery => String(surgery?.appointment_id) === String(appointmentId));
                setHasSurgeryForAppointment(hasSurgery);
            }
        } catch (error) {
            console.error("Error fetching pet surgeries:", error);
            showToast('error', t('failed_to_load_surgeries'));
            setPetSurgeries([]);
            setSurgeries([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString(),
            time: date.toLocaleTimeString(),
            full: date.toLocaleString()
        };
    };

    const handleAddSurgery = () => {
        setEditingSurgery(null);
        setSelectedSurgery(null);
        setSurgeryPrice("");
        setGeneralNote("");
        setShowModal(true);
    };

    const handleEditSurgery = (surgery) => {
        setEditingSurgery(surgery);
        setSelectedSurgery({
            value: surgery.surgery_id,
            label: surgery.surgery?.name,
            price: surgery.price || 0
        });
        setSurgeryPrice(surgery.price || "");

        // Extract note from the note array
        if (surgery.note && Array.isArray(surgery.note) && surgery.note.length > 0) {
            setGeneralNote(surgery.note[0].note || "");
        } else {
            setGeneralNote("");
        }

        setShowModal(true);
    };

    const handleDeleteClick = (surgeryId) => {
        setDeletingSurgeryId(surgeryId);
        setShowDeleteModal(true);
    };

    const handleDeleteSurgery = async () => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-surgery-delete/${deletingSurgeryId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setShowDeleteModal(false);
                fetchPetSurgeries();
                showToast('success', t('Deleted successfully'));
            }
        } catch (error) {
            console.error("Error deleting surgery:", error);
            showToast('error', error.response?.data?.message || t('failed to delete surgery'));
        } finally {
            setSubmitting(false);
            setDeletingSurgeryId(null);
        }
    };

    const handleSurgerySelect = (selectedOption) => {
        setSelectedSurgery(selectedOption);
        setSurgeryPrice(selectedOption?.price || "");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedSurgery) {
            showToast('error', t('please_select_a_surgery'));
            return;
        }

        // Process price: convert empty string to 0
        const processedPrice = surgeryPrice === "" || surgeryPrice === null || surgeryPrice === undefined
            ? 0
            : parseFloat(surgeryPrice);

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");

            let url, payload;

            if (editingSurgery) {
                // Update mode - surgery can be changed
                url = `${process.env.REACT_APP_API_URL}/pet-surgery-update/${editingSurgery.id}`;
                payload = {
                    appointment_id: appointmentId,
                    surgery_id: selectedSurgery.value, // Can be different from original
                    price: processedPrice,
                    note: generalNote.trim() || null
                };
            } else {
                // Create mode
                url = `${process.env.REACT_APP_API_URL}/pet-surgery`;
                payload = {
                    appointment_id: appointmentId,
                    surgeries: [{
                        surgery_id: selectedSurgery.value,
                        price: processedPrice
                    }],
                    note: generalNote.trim() || null
                };
            }

            console.log('Sending payload:', payload);

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.success) {
                setShowModal(false);
                fetchPetSurgeries();
                showToast('success', editingSurgery ? t('updated successf') : t('surgeries Added successfully'));
                // Reset form
                setSelectedSurgery(null);
                setSurgeryPrice("");
                setGeneralNote("");
                setEditingSurgery(null);
            }
        } catch (error) {
            console.error("Error saving surgery:", error);
            console.error("Error response:", error.response?.data);
            showToast('error', error.response?.data?.message || t('failed_to_save_surgery'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading surgeries')}</p>
            </div>
        );
    }

    const surgeryOptions = surgeries.map(s => ({
        value: s.id,
        label: `${s.name}`,
        price: s.price || 0
    }));

    const groupSurgeriesByAppointment = () => {
        const grouped = {};
        petSurgeries.forEach(surgery => {
            const appointmentIdKey = surgery.appointment_id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: surgery.appointment,
                    surgeries: []
                };
            }
            grouped[appointmentIdKey].surgeries.push(surgery);
        });
        return grouped;
    };

    return (
        <div>
            {/* Header with Add Button */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('surgeries list')}</h6>
                {isAppointmentOpen && !hasSurgeryForAppointment && hasPermission("Appointment Surgery Create") && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddSurgery}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={12} /> {t('add surgery')}
                    </Button>
                )}
            </div>

            {!petSurgeries || petSurgeries.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaSyringe size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no surgeries found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupSurgeriesByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, surgeries: appointmentSurgeries } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>

                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentSurgeries.length} {t('surgery(ies)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key === openNestedAccordion[appointmentIdKey] ? null : key
                                        })}
                                        className="nested-surgeries-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentSurgeries.map((surgery) => {
                                            if (!surgery) return null;
                                            const dateTime = formatDateTime(surgery.created_at);
                                            const noteText = surgery.note && Array.isArray(surgery.note) && surgery.note.length > 0
                                                ? surgery.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(surgery.id)} key={surgery.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaStethoscope className="text-primary" />
                                                                <Badge bg="success" className="px-3 py-2">
                                                                    {surgery.surgery?.name}
                                                                </Badge>

                                                                <div className="text-muted small">
                                                                    👨‍⚕️ <strong>Doctor:</strong> {surgery?.doctor?.name || 'Unknown Doctor'}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <MdDateRange size={14} />
                                                                        {moment(dateTime.date).format("DD MMM YYYY")}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <MdAccessTime size={14} />
                                                                        {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {hasPermission("Appointment Surgery edit") && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditSurgery(surgery)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff', border: 'none' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(surgery.id)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                                <div className="p-3 bg-light rounded">
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Surgery Name')}:</strong> {surgery.surgery?.name}
                                                                    </div>
                                                                    {surgery.surgery?.description && (
                                                                        <div className="mb-2">
                                                                            <strong className="text-muted">{t('Description')}:</strong> {surgery.surgery?.description}
                                                                        </div>
                                                                    )}
                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        {editingSurgery ? t('Edit Surgery') : t('Add Surgery')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t('Select Surgery')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                                options={surgeryOptions}
                                value={selectedSurgery}
                                onChange={handleSurgerySelect}
                                placeholder={t('select surgery')}
                                isSearchable
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                isDisabled={false} // Changed: Now enabled in edit mode as well
                            />
                            <small className="text-muted d-block mt-1">
                                {t('Select a surgery')}
                            </small>
                        </Form.Group>

                        {/* Price Field */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaMoneyBillWave className="text-success" /> {t('Price')}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                onFocus={(e) => e.target.select()}
                                value={surgeryPrice ?? ''}
                                onChange={(e) => setSurgeryPrice(e.target.value)}
                                onBlur={(e) => {
                                    if (e.target.value === "") {
                                        setSurgeryPrice("0");
                                    }
                                }}
                                placeholder={t('Enter price')}
                            />
                        </Form.Group>

                        {/* Note Field */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingSurgery ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('confirm delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <FaTrash size={48} className="text-danger mb-3" />
                    <h5>{t('delete surgery confirmation')}</h5>
                    <p className="text-muted mb-0">{t('this action cannot be undone')}</p>
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteSurgery} disabled={submitting}>
                        {submitting ? t('deleting') : t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}